import type { CSSProperties, FC, ReactNode } from 'react'
import { useEffect, useId, useRef } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Button, CircularProgress, TableCell, TableRow, Typography } from '@mui/material'

interface InlineConfirmRowProps {
  readonly id: string
  readonly confirmingId: string | null
  readonly onRequestConfirm: (id: string) => void
  readonly onConfirm: (id: string) => void
  readonly onCancel: () => void
  readonly isActioning: boolean
  readonly actionLabel: string
  readonly confirmMessage: string
  readonly children: ReactNode
  readonly columnCount: number
}

export const InlineConfirmRow: FC<InlineConfirmRowProps> = ({
  id,
  confirmingId,
  onRequestConfirm,
  onConfirm,
  onCancel,
  isActioning,
  actionLabel,
  confirmMessage,
  children,
  columnCount,
}) => {
  const confirmLabelId = useId()
  const cancelButtonRef = useRef<HTMLButtonElement>(null)
  const triggerButtonRef = useRef<HTMLButtonElement>(null)
  const isConfirming = confirmingId === id

  useEffect(() => {
    if (isConfirming) {
      cancelButtonRef.current?.focus()
    }
  }, [isConfirming])

  const handleCancel = (): void => {
    onCancel()
    setTimeout(() => {
      triggerButtonRef.current?.focus()
    }, 0)
  }

  const handleConfirm = (): void => {
    onConfirm(id)
  }

  const handleRequestConfirm = (): void => {
    onRequestConfirm(id)
  }

  return (
    <>
      <TableRow sx={{ ...rowSx, display: isConfirming ? 'none' : undefined }}>
        {children}
        <TableCell sx={actionCellSx}>
          <button
            ref={triggerButtonRef}
            type="button"
            onClick={handleRequestConfirm}
            aria-label={`${actionLabel} ${confirmMessage.replaceAll(/[?"]/g, '')}`}
            style={actionTriggerStyle}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              {actionLabel}
            </Typography>
          </button>
        </TableCell>
      </TableRow>

      {isConfirming && (
        <TableRow role="alertdialog" aria-labelledby={confirmLabelId} sx={confirmRowSx}>
          <TableCell colSpan={columnCount} sx={confirmCellSx}>
            <Box sx={confirmContentSx}>
              <Typography id={confirmLabelId} variant="body2" sx={{ color: 'text.secondary' }}>
                {confirmMessage}
              </Typography>
              <Box sx={confirmActionsSx}>
                {isActioning && <CircularProgress size={16} />}
                <Button
                  variant="contained"
                  size="small"
                  color="error"
                  onClick={handleConfirm}
                  disabled={isActioning}
                >
                  {actionLabel}
                </Button>
                <Button
                  ref={cancelButtonRef}
                  variant="text"
                  size="small"
                  onClick={handleCancel}
                  disabled={isActioning}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const rowSx: SxProps<Theme> = {
  '&:last-child td': {
    borderBottom: 0,
  },
}

const actionCellSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.8rem',
  borderBottomColor: 'divider',
  py: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
}

const actionTriggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  borderRadius: 4,
  paddingInline: 8,
  paddingBlock: 4,
  transition: 'all 150ms ease-in-out',
}

const confirmRowSx: SxProps<Theme> = {
  backgroundColor: 'action.hover',
}

const confirmCellSx: SxProps<Theme> = {
  borderBottomColor: 'divider',
  py: 1.5,
}

const confirmContentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  px: 1,
}

const confirmActionsSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
}
